import InlineCode from "@/components/InlineCode";
import { useAdminPerms } from "@/hooks/auth";
import { useOpenConfirmDialog } from "@/hooks/dialogs";
import { useEffect, useState } from "react";
import BanTemplatesInputDialog from "./BanTemplatesInputDialog";
import BanTemplatesListItem from "./BanTemplatesListItem";
import BanTemplatesListAddButton from "./BanTemplatesListAddButton";
import { AdminJailDurationType, SaveBanTemplatesResp } from "@shared/otherTypes";
import { templateFlags, TemplateFlagKind } from "./templateFlags";
import { sortSanctionTemplates } from "@shared/utils";
import { DndSortableGroup, DndSortableItem } from "@/components/dndSortable";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { BackendApiError, useBackendApi } from "@/hooks/fetch";
import { Loader2Icon, Settings2Icon } from "lucide-react";
import useSWR from "swr";
import { alphanumeric } from 'nanoid-dictionary';
import { customAlphabet } from "nanoid";
import { PageHeader } from "@/components/page-header";
const nanoid = customAlphabet(alphanumeric, 21);


//the admin jail duration is a superset of the ban one, so it covers both pages
export type BanTemplatesInputData = {
    id: string | null;
    reason: string;
    duration?: AdminJailDurationType; //warn templates have none
    flagged?: boolean;
}

//structural superset of the ban, admin jail and warn template shapes
type TemplatesDataType = {
    id: string;
    reason: string;
    duration?: AdminJailDurationType;
    anticheat?: boolean;
    analyse?: boolean;
};
type DataUpdaterFunc = (prev: TemplatesDataType[]) => TemplatesDataType[];

type TemplatesPageProps = {
    apiPath?: string;
    pageTitle?: string;
    kindLabel?: string;
    allowMinutes?: boolean; //admin jails can be set in minutes, bans cannot
    allowDuration?: boolean; //warns never expire, so they have no duration
    flagKind?: TemplateFlagKind;
};

function BanTemplatesPageInner({ apiPath, kindLabel, allowMinutes, allowDuration, flagKind }: Required<Omit<TemplatesPageProps, 'pageTitle'>>) {
    const flagInfo = templateFlags[flagKind];
    const isFlagged = (tpl: TemplatesDataType) => flagKind === 'analyse' ? !!tpl.analyse : !!tpl.anticheat;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [reasonInputDialogData, setReasonInputDialogData] = useState<BanTemplatesInputData | undefined>();
    const openConfirmDialog = useOpenConfirmDialog();
    const { hasPerm } = useAdminPerms();
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);

    const queryApi = useBackendApi<TemplatesDataType[]>({
        method: 'GET',
        path: apiPath,
        throwGenericErrors: true,
    });

    const saveApi = useBackendApi<SaveBanTemplatesResp, TemplatesDataType[]>({
        method: 'POST',
        path: apiPath,
        throwGenericErrors: true,
    });

    const swr = useSWR(apiPath, async () => {
        const data = await queryApi({});
        if (!data) throw new Error('No data returned');
        return data;
    }, {
        isPaused: () => (isSaving || !!saveError || isDialogOpen),
    });

    const updateBackend = async (updater: DataUpdaterFunc) => {
        setIsSaving(true);
        setSaveError(null);
        setIsSaveSuccessful(false);
        try {
            const groupingUpdater: DataUpdaterFunc = (prev) => sortSanctionTemplates(updater(prev));
            const data = await swr.mutate(groupingUpdater as any, false);
            const resp = await saveApi({ data });
            if (!resp) throw new Error('No data returned');
            setIsSaveSuccessful(true);
        } catch (error) {
            if (error instanceof BackendApiError || error instanceof Error) {
                setSaveError(error.message);
            } else {
                setSaveError(JSON.stringify(error));
            }
        } finally {
            setIsSaving(false);
        }
    }

    //Clear the save successful after 5 seconds
    useEffect(() => {
        if (!isSaveSuccessful) return;
        const timeout = setTimeout(() => {
            setIsSaveSuccessful(false);
        }, 5000);
        return () => clearTimeout(timeout);
    }, [isSaveSuccessful]);


    const shownTemplates = swr.data ? sortSanctionTemplates(swr.data) : undefined;

    //Drag and drop
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            updateBackend((items) => {
                const shown = sortSanctionTemplates(items);
                const oldIndex = shown.findIndex(x => x.id === active.id);
                const newIndex = shown.findIndex(x => x.id === over.id);

                return arrayMove(shown, oldIndex, newIndex);
            });
        }
    }

    const handleOnSave = ({ id, reason, duration, flagged }: BanTemplatesInputData) => {
        //undefined instead of false so untouched templates keep their shape
        const flag = flagged ? true : undefined;
        const buildTemplate = (templateId: string): TemplatesDataType => allowDuration
            ? { id: templateId, reason, duration, anticheat: flag }
            : { id: templateId, reason, analyse: flag };
        if (id) {
            updateBackend((prev) =>
                prev.map((item) => item.id === id ? buildTemplate(id) : item)
            );
        } else {
            updateBackend((prev) => [...prev, buildTemplate(nanoid(21))]);
        }
        setIsDialogOpen(false);
        setReasonInputDialogData(undefined);
    }

    //Handler for list actions
    const handleRemoveItem = (id: string) => {
        if (!id || !swr.data) return;
        const toBeRemoved = swr.data.find((item) => item.id === id);
        if (!toBeRemoved) return;
        openConfirmDialog({
            title: 'Remove Template',
            actionLabel: 'Remove',
            confirmBtnVariant: 'destructive',
            message: <>
                Are you sure you want to remove this {kindLabel} template?
            </>,
            onConfirm: () => {
                console.log('Remove confirmed', id);
                updateBackend((prev) => prev.filter((item) => item.id !== id));
            },
        });
    }
    const handleEditItem = (id: string) => {
        if (!id || !swr.data) return;
        console.log('Edit item', id);
        const item = swr.data.find((item) => item.id === id);
        setReasonInputDialogData(item && {
            id: item.id,
            reason: item.reason,
            duration: item.duration,
            flagged: isFlagged(item),
        });
        setIsDialogOpen(true);
    }
    const handleAddNewItem = () => {
        setReasonInputDialogData(undefined);
        setIsDialogOpen(true);
    }


    //Status display
    let statusNode: React.ReactNode;
    if (swr.error) {
        const retryFunc = () => swr.mutate();
        const errMsg = swr?.error?.message ?? 'unknown error';
        statusNode = <div className="inline-flex flex-wrap gap-1">
            <span className='text-destructive-inline'>Error loading: {errMsg}</span><br />
            <button className='underline hover:text-accent' onClick={retryFunc}>Try again?</button>
        </div>
    } else if (isSaving) {
        statusNode = <span className="text-success-inlinex animate-pulse">
            Saving...
        </span>
    } else if (saveError) {
        const retryFunc = () => {
            if (!swr.data) return;
            updateBackend(() => swr.data!);
        }
        const errMsg = saveError ?? 'unknown error';
        statusNode = <div className="inline-flex flex-wrap gap-1">
            <span className='text-destructive-inline'>Error saving: {errMsg}</span><br />
            <button className='underline hover:text-accent' onClick={retryFunc}>Try again?</button>
        </div>
    } else if (isSaveSuccessful) {
        statusNode = <span className="text-success-inline">
            Saved.
        </span>
    } else if (swr.isLoading || swr.isValidating) {
        statusNode = <span className="text-muted-foreground">
            Loading...
        </span>
    }

    //Rendering
    const canEdit = hasPerm('settings.write');
    return <>
        <div className="space-y-4 w-full max-w-screen-lg mx-auto">
            <p className="px-2 md:px-0">
                Here you can configure {kindLabel} reasons{allowDuration ? ' and durations' : ''} that will appear as dropdown options in the player modal. <br />
                This is useful for common reasons that happen frequently, like violation of your server rules. <br />
                {canEdit ? (
                    <span className="text-muted-foreground italic">
                        TIP: You can also drag and drop to reorder the list. <br />
                    </span>
                ) : (
                    <span className="text-warning-inline">
                        You need the <InlineCode className="text-warning-inline">Settings: Change</InlineCode> permission to edit these reasons.
                    </span>
                )}
            </p>
            <div className="space-y-2">
                <div className="flex flex-wrap justify-between text-muted-foreground px-2 md:px-0">
                    <span className="shrink-0">Configured reasons: {swr.data?.length ?? 0}</span>
                    {statusNode}
                </div>

                {!swr.data && !saveError && (
                    <div className="text-muted-foreground text-lg md:text-2xl text-center my-4 px-2 md:px-0">
                        <Loader2Icon className="inline animate-spin h-8" />Loading...
                    </div>
                )}
                {shownTemplates && (
                    <DndSortableGroup
                        className="space-y-2 border p-2 xs:rounded-lg"
                        ids={shownTemplates.map((item) => item.id)}
                        onDragEnd={handleDragEnd}
                    >
                        {!shownTemplates.length ? (
                            <div className="text-muted-foreground text-lg md:text-2xl text-center my-4 px-2 md:px-0">
                                No templates configured yet.
                            </div>
                        ) : shownTemplates.map((item) => (
                            <DndSortableItem
                                key={item.id}
                                id={item.id}
                                disabled={!canEdit}
                            >
                                <BanTemplatesListItem
                                    id={item.id}
                                    reason={item.reason}
                                    duration={item.duration}
                                    flag={isFlagged(item) ? flagInfo : undefined}
                                    onEdit={handleEditItem}
                                    onRemove={handleRemoveItem}
                                    disabled={!canEdit}
                                />
                            </DndSortableItem>
                        ))}
                        <BanTemplatesListAddButton onClick={handleAddNewItem} disabled={!canEdit} />
                    </DndSortableGroup>
                )}
            </div>
        </div>
        <BanTemplatesInputDialog
            key={reasonInputDialogData?.id}
            reasonData={reasonInputDialogData}
            onSave={handleOnSave}
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            kindLabel={kindLabel}
            allowMinutes={allowMinutes}
            allowDuration={allowDuration}
            flagKind={flagKind}
        />
    </>;
}


export default function BanTemplatesPage({
    apiPath = '/settings/banTemplates',
    pageTitle = 'Ban Templates',
    kindLabel = 'ban',
    allowMinutes = false,
    allowDuration = true,
    flagKind = 'anticheat',
}: TemplatesPageProps) {
    return (
        <div className="w-full mb-10">
            <PageHeader
                icon={<Settings2Icon />}
                title={pageTitle}
                parentName="Settings"
                parentLink="/settings#templates"
            />
            <div className="px-0 xs:px-3 md:px-0 flex flex-row gap-2 w-full">
                <BanTemplatesPageInner
                    apiPath={apiPath}
                    kindLabel={kindLabel}
                    allowMinutes={allowMinutes}
                    allowDuration={allowDuration}
                    flagKind={flagKind}
                />
            </div>
        </div>
    )
}
